import type { SnowplowDataAdapters, SnowplowUserSnapshot } from '@nextmar/snowplow-sdk';
import { getCapturedSnowplowUserId } from './capturedUserId';
import type { User } from '@/api/types/public';
import { hasSensorsInitialized } from '@/reporting/reliability/identityGate';
import { getSensorsIdentity, readLiveSensorsAnonymousId } from '@/reporting/utils/sensorsIdentity';
import { ZL_USERINFO } from '@/utils/constants';
import { getPlatformLanguage } from '@/utils/locale-cookie';
import { getCookie, isInZeloolApp } from '@/utils/tools';

/** Z 登录 token 的既有 localStorage key。 */
const ZL_TOKEN_STORAGE_KEY = 'zl-token';

/** SDK 允许接收的 Z 归因字段；expires 只供 SDK 判断有效期。 */
const ZEELOOL_ATTRIBUTION_KEYS = [
  'expires',
  'pub_zl_utm_source',
  'pub_zl_utm_medium',
  'pub_zl_utm_campaign',
  'pub_zl_utm_campaignid',
  'pub_zl_referrer',
  'pub_zl_page',
  'pub_landing_page',
] as const;

/** 带 Z 归因全局值的最小 Window 形状。 */
interface ZeeloolAttributionWindow {
  latestChannelAttribute?: Readonly<Record<string, unknown>>;
}

/** 把业务身份值规范为 SDK 使用的非空字符串。 */
function normalizeIdentityValue(value: unknown): string | undefined {
  if (
    value === '' ||
    value === null ||
    value === undefined ||
    value === 'null' ||
    value === 'undefined'
  ) {
    return undefined;
  }

  return String(value);
}

/** 按旧公共属性合同把异常会员等级映射为 9999。 */
function resolveMemberLevel(value: unknown): string {
  const normalizedValue = normalizeIdentityValue(value);
  if (!normalizedValue) return '9999';

  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= -1 ? normalizedValue : '9999';
}

/** 回跳时使用后端刚写入的 UUID；其他页面仅在当前登录账号一致时使用。 */
function readLoginCookieUuid(): string | undefined {
  try {
    if (typeof window === 'undefined' || isInZeloolApp()) return undefined;
    const uuid = normalizeIdentityValue(getCookie('uuid'));
    if (!uuid) return undefined;
    if (new URLSearchParams(window.location.search).get('token')) return uuid;
    if (!localStorage.getItem(ZL_TOKEN_STORAGE_KEY)) return undefined;
    const user = JSON.parse(localStorage.getItem(ZL_USERINFO) ?? '{}') as User | null;
    return user?.uuid === uuid ? uuid : undefined;
  } catch {
    return undefined;
  }
}

/** 安全读取用户缓存；回跳早期缓存尚未写入时保留 Cookie 提供的最小埋点身份。 */
function getUserSnapshot(): SnowplowUserSnapshot {
  const cookieUuid = readLoginCookieUuid();
  const fallback = cookieUuid ? { uuid: cookieUuid, isLoggedIn: true } : {};
  try {
    const storage = globalThis.localStorage;
    const rawUser = storage?.getItem(ZL_USERINFO);
    let parsedUser = JSON.parse(rawUser ?? '{}') as Partial<User> | null;
    if (!parsedUser || typeof parsedUser !== 'object' || Array.isArray(parsedUser)) {
      return fallback;
    }
    // 不将新账号 UUID 与旧账号尚未更新的客户、邮箱和会员资料拼接。
    if (cookieUuid && normalizeIdentityValue(parsedUser.uuid) !== cookieUuid) parsedUser = {};

    const uuid = cookieUuid ?? normalizeIdentityValue(parsedUser.uuid);
    const customerId = normalizeIdentityValue(parsedUser.id);
    const email = normalizeIdentityValue(parsedUser.email);
    const phone = normalizeIdentityValue(parsedUser.telephone);

    return {
      ...(uuid ? { uuid } : {}),
      ...(customerId ? { customerId } : {}),
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
      isLoggedIn: !!cookieUuid || !!storage?.getItem(ZL_TOKEN_STORAGE_KEY),
      memberLevel: resolveMemberLevel(parsedUser.member_level),
      isPaid: !!parsedUser.last_pay_time,
    };
  } catch {
    return fallback;
  }
}

/** 安全复制当前窗口中的批准归因字段，不保留业务全局对象引用。 */
function getAttributionSnapshot(): Readonly<Record<string, unknown>> {
  try {
    const currentWindow = globalThis.window as unknown as ZeeloolAttributionWindow | undefined;
    const source = currentWindow?.latestChannelAttribute;
    if (!source || typeof source !== 'object' || Array.isArray(source)) return {};

    const snapshot: Record<string, unknown> = {};
    for (const key of ZEELOOL_ATTRIBUTION_KEYS) {
      const value = source[key];
      if (value !== undefined) snapshot[key] = value;
    }
    return snapshot;
  } catch {
    return {};
  }
}

/** 创建同步适配器；SignUp 捕获时关闭 Cookie 兜底，保留真实 SDK 身份绑定校验。 */
export function createZeeloolSnowplowDataAdapters(useLoginCookie = true): SnowplowDataAdapters {
  return {
    /** 仅冻结已经成功显式同步的客户ID，配置前资料不自动成为根uid。 */
    getTrackerUserId: getCapturedSnowplowUserId,
    /** UUID Cookie 不代表匿名身份已就绪，仍须等待本窗口 live anonymous ID。 */
    isSensorsInitialized: () => hasSensorsInitialized() && !!readLiveSensorsAnonymousId(),
    /** 回跳 UUID 可先于 SDK 就绪；anonymousId 仍只来自本窗口 live SDK。 */
    getIdentity() {
      const cookieUuid = useLoginCookie ? readLoginCookieUuid() : undefined;
      const fallback = cookieUuid ? { distinctId: cookieUuid } : {};
      const anonymousId = readLiveSensorsAnonymousId();
      if (!anonymousId) return fallback;
      try {
        return { ...getSensorsIdentity(), anonymousId, ...fallback };
      } catch {
        return { anonymousId, ...fallback };
      }
    },
    /** 安全读取用户、会员、支付和登录状态。 */
    getUser() {
      return getUserSnapshot();
    },
    /** 安全读取当前归因及过期元数据。 */
    getAttribution() {
      return getAttributionSnapshot();
    },
    /** 安全读取当前平台语言。 */
    getPlatformLanguage() {
      try {
        const language = getPlatformLanguage();
        return typeof language === 'string' ? language : undefined;
      } catch {
        return undefined;
      }
    },
  };
}
